import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkle, ChartLineUp, Timer } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#E1252A", "#09090B", "#FFAB00", "#00C853", "#002FA7", "#A1A1AA"];

export default function Analytics() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [depts, setDepts] = useState({});
  const [summary, setSummary] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [o, d] = await Promise.all([api.get("/analytics/overview"), api.get("/departments")]);
      setOverview(o.data);
      setDepts(Object.fromEntries(d.data.map((dd) => [dd.dept_id, dd.name])));
      try {
        const a = await api.get("/analytics/approvals");
        setApprovals(a.data);
      } catch {}
    })();
  }, []);

  const genSummary = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/analytics/executive-summary");
      setSummary(data);
    } finally {
      setBusy(false);
    }
  };

  if (!["ADMIN", "MANAGER"].includes(user.role)) {
    return <div className="p-8 text-sm text-zinc-500">Only Admins and Managers can see analytics.</div>;
  }

  const statusData = (overview?.tasks_by_status || []).map((s) => ({ name: s.status, value: s.count }));
  const deptData = (overview?.tasks_by_department || []).map((d) => ({ name: depts[d.department_id] || "—", value: d.count }));
  const throughputData = approvals ? [
    { name: "Completed", AI: approvals.throughput.ai.completed, Human: approvals.throughput.human.completed },
    { name: "In progress", AI: approvals.throughput.ai.active, Human: approvals.throughput.human.active },
  ] : [];
  const fmtDur = (h) => {
    if (h == null) return "—";
    if (h < 1 / 60) return "<1m";
    if (h < 1) return `${Math.round(h * 60)}m`;
    return `${Math.round(h * 10) / 10}h`;
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="analytics-page">
      <div className="mb-6 flex items-center gap-3">
        <ChartLineUp size={28} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Insights</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold">Marketplace analytics</h1>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        <KPI label="Total tasks" value={overview?.total_tasks ?? 0} testid="kpi-total" />
        <KPI label="Completed" value={overview?.completed ?? 0} accent="#00C853" testid="kpi-completed" />
        <KPI label="Users" value={overview?.users_count ?? 0} testid="kpi-users" />
        <KPI label="Credits live" value={overview?.total_credits_circulating ?? 0} accent="#E1252A" testid="kpi-credits" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="border border-border p-4 bg-background" data-testid="chart-status">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Tasks by status</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="name" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip />
              <Bar dataKey="value" fill="#E1252A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border p-4 bg-background" data-testid="chart-dept">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Tasks by department</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={deptData} dataKey="value" nameKey="name" outerRadius={90} label>
                {deptData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Approval analytics */}
      {approvals && (
        <div className="mb-8" data-testid="approval-analytics">
          <div className="flex items-center gap-2 mb-3">
            <Timer size={18} className="text-[#E1252A]" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Governance</div>
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Approval cycle times & throughput</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
            <KPI label="Avg cycle time" value={fmtDur(approvals.overall_avg_hours)} accent="#002FA7" testid="kpi-avg-cycle" />
            <KPI label="Pending approvals" value={approvals.total_pending} accent="#FFAB00" testid="kpi-pending-approvals" />
            <KPI label="AI tasks done" value={approvals.throughput.ai.completed} accent="#E1252A" testid="kpi-ai-done" />
            <KPI label="Human tasks done" value={approvals.throughput.human.completed} accent="#00C853" testid="kpi-human-done" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-border bg-background" data-testid="cycle-time-table">
              <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-zinc-500">Cycle time by approval stage</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-border">
                    <th className="px-4 py-2 font-medium">Stage</th>
                    <th className="px-4 py-2 font-medium text-right">Avg</th>
                    <th className="px-4 py-2 font-medium text-right">Pending</th>
                    <th className="px-4 py-2 font-medium text-right">Approved</th>
                    <th className="px-4 py-2 font-medium text-right">Rejected</th>
                  </tr>
                </thead>
                <tbody>
                  {approvals.cycle_times.map((c) => (
                    <tr key={c.type} className="border-b border-border/60" data-testid={`cycle-row-${c.type}`}>
                      <td className="px-4 py-2.5">{c.label}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmtDur(c.avg_hours)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-amber-600">{c.pending}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-600">{c.approved}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-[#E1252A]">{c.rejected}</td>
                    </tr>
                  ))}
                  {approvals.cycle_times.length === 0 && (
                    <tr><td className="px-4 py-6 text-center text-zinc-500" colSpan={5}>No approvals yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border border-border p-4 bg-background" data-testid="chart-throughput">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">AI vs human throughput</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={throughputData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="name" fontSize={10} />
                  <YAxis fontSize={10} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="AI" fill="#E1252A" />
                  <Bar dataKey="Human" fill="#002FA7" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Executive summary */}
      <div className="border border-border p-6 bg-surface/40" data-testid="exec-summary-block">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">AI Executive Summary</div>
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Quick read for leadership</h2>
          </div>
          <Button onClick={genSummary} disabled={busy} variant="outline" className="rounded-sm" data-testid="gen-exec-summary-btn">
            <Sparkle size={14} className="mr-1" /> {busy ? "Generating…" : "Generate"}
          </Button>
        </div>
        {summary?.summary ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="exec-summary-text">{summary.summary}</p>
        ) : (
          <p className="text-sm text-zinc-500">Click Generate to produce a Claude-powered executive summary based on current marketplace data.</p>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, accent, testid }) {
  return (
    <div className="border border-border p-4 bg-background" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="text-2xl sm:text-3xl mt-2 font-bold font-mono tracking-tight" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}
